import apiClient from './index';
import { SequenceAnalysisTask } from './types';

// 轮询任务结果的函数
export const pollTaskResult = async (taskId: string, maxAttempts: number = 30, intervalMs: number = 2000): Promise<any> => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      // 使用公开的状态接口，不需要认证 - 从环境变量获取API地址
      const apiBase = import.meta.env.VITE_TASK_MANAGER_URL?.replace(/\/$/, '') || '/task_manager';
      const response = await fetch(`${apiBase}/tasks/${taskId}/status`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const task = await response.json();
      
      console.log(`轮询任务 ${taskId} (第${attempt + 1}/${maxAttempts}次):`, {
        status: task.status,
        progress: task.progress,
        hasResult: !!task.result,
        resultKeys: task.result ? Object.keys(task.result).length : 0
      });
      
      // 检查任务是否完成
      if (task.status === 'completed') {
        // 增强结果验证：检查result是否有效
        if (!task.result) {
          console.warn(`⚠️ 任务 ${taskId} 状态为completed但result为null，继续等待...`);
          if (attempt < maxAttempts - 1) {
            await new Promise(resolve => setTimeout(resolve, intervalMs));
            continue;  // 继续轮询
          }
        } else if (typeof task.result === 'object' && Object.keys(task.result).length === 0) {
          console.warn(`⚠️ 任务 ${taskId} 状态为completed但result为空对象，继续等待...`);
          if (attempt < maxAttempts - 1) {
            await new Promise(resolve => setTimeout(resolve, intervalMs));
            continue;  // 继续轮询
          }
        }
        
        // result有效，返回结果
        console.log(`✅ 任务 ${taskId} 完成，result有效`);
        return task.result;
      } else if (task.status === 'failed') {
        throw new Error(task.error || '任务执行失败');
      }
      
      // 等待后继续轮询
      if (attempt < maxAttempts - 1) {
        await new Promise(resolve => setTimeout(resolve, intervalMs));
      }
    } catch (error) {
      console.error(`轮询任务 ${taskId} 第 ${attempt + 1} 次失败:`, error);
      if (attempt === maxAttempts - 1) {
        throw error;
      }
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
  }
  
  throw new Error('任务轮询超时，请在任务管理页面查看结果');
};

// 序列处理接口类型定义
export interface SequenceAlignRequest {
  sequences: string[];
  names?: string[];
  sequence_type: string;
}

export interface AlignmentRequest {
  sequences?: string[];
  fasta_content?: string;  
  names?: string[];
  sequence_type: 'dna' | 'rna' | 'protein';
  gap_open_penalty?: number;
  gap_extension_penalty?: number;
}

export interface AlignmentResult {
  aligned_sequences: string[];
  names?: string[];
  alignment_score?: number;
  runtime: number;
  method_used: string;
  similarity_matrix?: number[][];
  newick_tree?: string;
  phyloxml_tree?: string;
}

// 批量序列处理请求类型
export interface BatchContentRequest {
  content: string; // FASTA格式或纯文本序列
}

export interface BatchValidationRequest extends BatchContentRequest {
  sequence_type?: string; // 可选的序列类型指定
}

export interface BatchContentWithTypeRequest extends BatchContentRequest {
  sequence_type?: string; // 用于指定核酸类型：'dna' 或 'rna'
}

// 序列处理服务
class SequenceService {
  // 序列比对
  async alignSequences(data: SequenceAlignRequest): Promise<any> {
    try {
      const response = await apiClient.post('/sequence_processing/align_json', {
        sequences: data.sequences,
        names: data.names,
        sequence_type: data.sequence_type
      });

      // 若后端经过统一格式化返回统一结构，抽取第一条
      if (response?.results && Array.isArray(response.results) && response.results.length > 0) {
        const firstResult = response.results[0]?.result ?? response.results[0];
        return {
          ...firstResult,
          meta: response.meta,
          artifacts: response.artifacts,
        };
      }

      return response;
    } catch (error) {
      console.error('多序列比对任务执行失败:', error);
      throw error;
    }
  }

  // 获取所有可用的序列分析任务
  async getAnalysisTasks(): Promise<SequenceAnalysisTask[]> {
    console.log('正在请求任务列表API...');
    try {
      // apiClient 的响应拦截器会直接返回 data
      const data = await apiClient.get<SequenceAnalysisTask[]>(
        '/sequence_processing/tasks'
      );
      if (!Array.isArray(data)) {
        throw new Error('任务列表响应格式不正确');
      }
      return data;
    } catch (error) {
      console.error('获取序列分析任务失败:', error);
      // 透出错误，让 useQuery 的 onError 正确触发，避免误判为空列表
      throw error;
    }
  }

  // 通用任务执行接口 - 通过任务管理器
  async executeAnalysisTask(
    taskId: string,
    data: { content?: string; sequence?: string; handler_name?: string; [key: string]: any },
    handlerNameOverride?: string
  ): Promise<any> {
    try {
      // 为单序列输入适配批量格式
      let requestData = data;
      
      // 如果传入的是单序列格式，则转换为批量格式
      if (data.sequence && !data.content) {
        requestData = {
          ...data,
          content: data.sequence // 将单序列转为content字段
        };
        delete requestData.sequence;
      }

      // 优先使用任务对象返回的 handler_name；兼容传入 taskIdOrHandler 为 handler_name 的情况
      const handlerName = handlerNameOverride || requestData.handler_name || taskId;

      const taskData = {
        name: `序列分析任务: ${taskId}`,
        service_name: 'sequence_service',
        task_type: 'sequence_analysis',
        handler_name: handlerName,
        parameters: requestData,
        dependencies: []
      };

      console.log(`通过任务管理器执行任务: ${taskId}, handler: ${handlerName}`);
      // Call task manager directly since proxy routing may not be configured - 从环境变量获取API地址
      const apiBase = import.meta.env.VITE_TASK_MANAGER_URL?.replace(/\/$/, '') || '/task_manager';
      const response = await apiClient.post(`${apiBase}/tasks/`, taskData);
      console.log('executeAnalysisTask 响应:', response);
      return response;
    } catch (error) {
      console.error(`任务 ${taskId} 执行失败:`, error);
      throw error;
    }
  }
}

// 创建服务实例
const sequenceService = new SequenceService();

// 同时支持默认导出和命名导出
export { sequenceService };
export default sequenceService;